'use client';

import { useMemo, useState } from 'react';
import Link from 'next/link';

export interface GlossaryItem {
  term: string;
  slug: string;
  description: string;
  letter: string;
}

const ALPHABET = '#ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

export default function GlossaryBrowser({ items }: { items: GlossaryItem[] }) {
  const [query, setQuery] = useState('');

  const q = query.trim().toLowerCase();

  const filtered = useMemo(() => {
    if (!q) return items;
    return items.filter(
      (i) => i.term.toLowerCase().includes(q) || i.description.toLowerCase().includes(q),
    );
  }, [items, q]);

  // Group by letter (sorted A→Z, '#' last)
  const groups = useMemo(() => {
    const map = new Map<string, GlossaryItem[]>();
    for (const item of filtered) {
      const letter = item.letter || '#';
      if (!map.has(letter)) map.set(letter, []);
      map.get(letter)!.push(item);
    }
    for (const list of map.values()) list.sort((a, b) => a.term.localeCompare(b.term));
    return ALPHABET.filter((l) => map.has(l)).map((l) => ({ letter: l, items: map.get(l)! }));
  }, [filtered]);

  const availableLetters = useMemo(() => new Set(items.map((i) => i.letter || '#')), [items]);

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-8">
      {/* Search */}
      <div className="relative max-w-xl mx-auto -mt-8 mb-8">
        <div className="relative">
          <svg
            className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5"
            style={{ color: 'var(--color-t-body)', opacity: 0.5 }}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11a6 6 0 11-12 0 6 6 0 0112 0z" />
          </svg>
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search HR, payroll & workforce terms…"
            aria-label="Search glossary terms"
            className="w-full pl-12 pr-4 py-3.5 rounded-xl text-base shadow-sm focus:outline-none focus:ring-2 transition"
            style={{
              backgroundColor: 'var(--color-t-section-light)',
              color: 'var(--color-t-body)',
              border: '1px solid color-mix(in srgb, var(--color-t-primary) 18%, transparent)',
            }}
          />
        </div>
      </div>

      {/* A–Z nav */}
      <nav aria-label="Jump to letter" className="flex flex-wrap justify-center gap-1.5 mb-10">
        {ALPHABET.map((letter) => {
          const has = availableLetters.has(letter) && groups.some((g) => g.letter === letter);
          return has ? (
            <a
              key={letter}
              href={`#letter-${letter}`}
              className="w-9 h-9 inline-flex items-center justify-center rounded-lg text-sm font-bold transition-colors"
              style={{
                backgroundColor: 'color-mix(in srgb, var(--color-t-primary) 8%, transparent)',
                color: 'var(--color-t-primary)',
              }}
            >
              {letter}
            </a>
          ) : (
            <span
              key={letter}
              className="w-9 h-9 inline-flex items-center justify-center rounded-lg text-sm font-bold"
              style={{ color: 'var(--color-t-body)', opacity: 0.25 }}
            >
              {letter}
            </span>
          );
        })}
      </nav>

      {/* Results */}
      {groups.length === 0 ? (
        <p className="text-center text-lg py-16" style={{ color: 'var(--color-t-body)', opacity: 0.7 }}>
          No terms match “{query}”.
        </p>
      ) : (
        <div className="space-y-12 pb-16">
          {groups.map((group) => (
            <section key={group.letter} id={`letter-${group.letter}`} className="scroll-mt-24">
              <h2
                className="text-2xl font-extrabold mb-5 pb-2 border-b"
                style={{
                  color: 'var(--color-t-primary)',
                  borderColor: 'color-mix(in srgb, var(--color-t-primary) 15%, transparent)',
                }}
              >
                {group.letter}
              </h2>
              <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {group.items.map((item) => (
                  <Link
                    key={item.slug}
                    href={`/glossary/${item.slug}`}
                    className="block p-5 rounded-xl transition-all hover:-translate-y-0.5 hover:shadow-md"
                    style={{
                      backgroundColor: 'var(--color-t-section-light)',
                      border: '1px solid color-mix(in srgb, var(--color-t-primary) 10%, transparent)',
                    }}
                  >
                    <h3 className="text-base font-bold mb-1.5" style={{ color: 'var(--color-t-primary)' }}>
                      {item.term}
                    </h3>
                    <p
                      className="text-sm leading-relaxed line-clamp-3"
                      style={{ color: 'var(--color-t-body)', opacity: 0.8 }}
                    >
                      {item.description}
                    </p>
                  </Link>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
